<script>
import axios from '@/utils/axios';

const DEFAULT_FORM = () => ({
  id: null,
  name: '',
  start_time: '',
  end_time: '',
  grace_minutes: 0,
  full_day_hours: 8,
  half_day_hours: 4,
  short_day_hours: 2,
  multiplier: 1,
  description: '',
  working_days: [],
});

export default {
  inject: ['setLoading'],
  emits: ['updated'],
  data() {
    return {
      form: DEFAULT_FORM(),
      errors: {},
      deleteItem: null,
      allDays: [
        { key: 'mon', label: 'Mon' },
        { key: 'tue', label: 'Tue' },
        { key: 'wed', label: 'Wed' },
        { key: 'thu', label: 'Thu' },
        { key: 'fri', label: 'Fri' },
        { key: 'sat', label: 'Sat' },
        { key: 'sun', label: 'Sun' },
      ],
    };
  },
  methods: {
    add() {
      this.resetForm();
    },

    edit(shift) {
      this.resetForm();
      this.form.id = shift.id;
      this.form.name = shift.name || '';
      this.form.start_time = this.toInputTime(shift.start_time);
      this.form.end_time = this.toInputTime(shift.end_time);
      this.form.grace_minutes = shift.grace_minutes ?? 0;
      this.form.full_day_hours = shift.full_day_hours ?? 0;
      this.form.half_day_hours = shift.half_day_hours ?? 0;
      this.form.short_day_hours = shift.short_day_hours ?? 0;
      this.form.multiplier = shift.multiplier ?? 1;
      this.form.description = shift.description || '';
      this.form.working_days = this.parseDays(shift.working_days);
    },

    setDelete(shift) {
      this.deleteItem = shift;
    },

    toggleDay(key) {
      const idx = this.form.working_days.indexOf(key);
      if (idx === -1) this.form.working_days.push(key);
      else this.form.working_days.splice(idx, 1);
    },

    isDayActive(key) {
      return this.form.working_days.includes(key);
    },

    // "10:00:00" -> "10:00" (for <input type=time>)
    toInputTime(time) {
      if (!time) return '';
      return time.slice(0, 5);
    },

    toApiTime(time) {
        if (!time) return null;
        return time.length === 5 ? `${time}:00` : time;
        },

    // API JSON string -> array
    parseDays(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },

    buildPayload() {
      return {
        name: this.form.name,
        start_time: this.toApiTime(this.form.start_time),   
        end_time: this.toApiTime(this.form.end_time),
        grace_minutes: this.form.grace_minutes,
        full_day_hours: this.form.full_day_hours,
        half_day_hours: this.form.half_day_hours,
        short_day_hours: this.form.short_day_hours,
        multiplier: this.form.multiplier,
        description: this.form.description || null,
        working_days: JSON.stringify(this.form.working_days),
      };
    },

    async save() {
      this.errors = {};
      this.setLoading(true);
      try {
        let response;
        if (this.form.id) {
          response = await axios.put(`/hrms/shifts/${this.form.id}`, this.buildPayload());
        } else {
          response = await axios.post('/hrms/shifts', this.buildPayload());
        }
        this.$swalToast(response.data.message);
        this.$emit('updated');
        this.$closeModal('hrm_shift_modal');
        this.resetForm();
      } catch (error) {
        if (error.type === 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async deleteShift() {
      this.setLoading(true);
      try {
        const response = await axios.delete(`/hrms/shifts/${this.deleteItem.id}`);
        this.$swalToast(response.data.message);
        this.$emit('updated');
        this.$closeModal('hrm_shift_delete_modal');
      } catch (error) {
        if (error.type === 'validation') {
          this.$swalToast(error.errors?.id?.[0] || error.message, 'error');
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    resetForm() {
      this.form = DEFAULT_FORM();
      this.errors = {};
    },
  },
};
</script>

<template>
  <!-- Add/Edit Modal -->
  <div class="modal fade" id="hrm_shift_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ form.id ? 'Edit' : 'Add' }} Shift</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="save">
          <div class="modal-body pb-0">
            <div class="row">

              <!-- Name -->
              <div class="col-12 mb-3">
                <label class="form-label">Name <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="form.name" placeholder="e.g. Day Shift" />
                <div v-if="errors.name" class="text-danger small mt-1">{{ errors.name[0] }}</div>
              </div>

              <!-- Start / End time -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Start Time <span class="text-danger">*</span></label>
                <input type="time" class="form-control" v-model="form.start_time" />
                <div v-if="errors.start_time" class="text-danger small mt-1">{{ errors.start_time[0] }}</div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">End Time <span class="text-danger">*</span></label>
                <input type="time" class="form-control" v-model="form.end_time" />
                <div v-if="errors.end_time" class="text-danger small mt-1">{{ errors.end_time[0] }}</div>
              </div>

              <!-- Working Days -->
              <div class="col-12 mb-3">
                <label class="form-label">Working Days</label>
                <div class="d-flex flex-wrap gap-2">
                  <button
                    v-for="day in allDays"
                    :key="day.key"
                    type="button"
                    class="day-pill"
                    :class="{ active: isDayActive(day.key) }"
                    @click="toggleDay(day.key)"
                  >{{ day.label }}</button>
                </div>
                <div v-if="errors.working_days" class="text-danger small mt-1">{{ errors.working_days[0] }}</div>
              </div>

              <!-- Grace minutes / Multiplier -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Grace Minutes</label>
                <input type="number" min="0" class="form-control" v-model.number="form.grace_minutes" />
                <div v-if="errors.grace_minutes" class="text-danger small mt-1">{{ errors.grace_minutes[0] }}</div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Multiplier</label>
                <input type="number" min="0" step="0.1" class="form-control" v-model.number="form.multiplier" />
                <div v-if="errors.multiplier" class="text-danger small mt-1">{{ errors.multiplier[0] }}</div>
              </div>

              <!-- Hours -->
              <div class="col-md-4 mb-3">
                <label class="form-label">Full Day Hours</label>
                <input type="number" min="0" step="any" class="form-control" v-model.number="form.full_day_hours" />
                <div v-if="errors.full_day_hours" class="text-danger small mt-1">{{ errors.full_day_hours[0] }}</div>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Half Day Hours</label>
                <input type="number" min="0" step="any" class="form-control" v-model.number="form.half_day_hours" />
                <div v-if="errors.half_day_hours" class="text-danger small mt-1">{{ errors.half_day_hours[0] }}</div>
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Short Day Hours</label>
                <input type="number" min="0" step="any" class="form-control" v-model.number="form.short_day_hours" />
                <div v-if="errors.short_day_hours" class="text-danger small mt-1">{{ errors.short_day_hours[0] }}</div>
              </div>

              <!-- Description -->
              <div class="col-12 mb-3">
                <label class="form-label">Description</label>
                <textarea class="form-control" rows="2" v-model="form.description" placeholder="Enter description (optional)"></textarea>
                <div v-if="errors.description" class="text-danger small mt-1">{{ errors.description[0] }}</div>
              </div>

            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-primary">{{ form.id ? 'Update' : 'Save' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade" id="hrm_shift_delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">You want to delete this shift? This can't be undone.</p>
          <div class="d-flex justify-content-center">
            <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
            <a href="javascript:void(0);" class="btn btn-danger" @click="deleteShift">Yes, Delete</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.day-pill {
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #475569;
  font-size: 12.5px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: all .15s ease;
}
.day-pill:hover {
  border-color: #c7d2fe;
}
.day-pill.active {
  background: #6366f1;
  border-color: #6366f1;
  color: #fff;
}
</style>